import type {
  MealPlanEntry,
  MealPlanEntryWriteInput,
  MealSlot,
  RecipeListItem,
} from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';
import { mealSlotLabel } from '@/features/shared/labels';
import { RecipeSearchList } from '@/features/shared/recipe-picker';
import { STANDARD_SLOTS, type SlotTarget } from './week-grid';

export type EntryDialogState =
  | { mode: 'create'; target: SlotTarget }
  | { mode: 'edit'; entry: MealPlanEntry }
  | { mode: 'move'; entry: MealPlanEntry }
  | null;

export function EntryDialog({
  state,
  onClose,
  onSubmit,
  saving,
}: {
  state: EntryDialogState;
  onClose: () => void;
  onSubmit: (input: Omit<MealPlanEntryWriteInput, 'householdId'>) => void;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'recipe' | 'text'>('recipe');
  const [recipe, setRecipe] = useState<RecipeListItem | null>(null);
  const [recipeTitle, setRecipeTitle] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState<MealSlot>('DINNER');
  const [customSlotName, setCustomSlotName] = useState('');
  const [servings, setServings] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!state) return;
    if (state.mode === 'create') {
      setKind('recipe');
      setRecipe(null);
      setRecipeTitle(null);
      setTitle('');
      setDate(state.target.date);
      setSlot(state.target.slot);
      setCustomSlotName(state.target.customSlotName ?? '');
      setServings('');
      setNote('');
    } else {
      const e = state.entry;
      setKind(e.recipeId ? 'recipe' : 'text');
      setRecipe(null);
      setRecipeTitle(e.recipeTitle);
      setTitle(e.title ?? '');
      setDate(e.date);
      setSlot(e.slot);
      setCustomSlotName(e.customSlotName ?? '');
      setServings(e.servings ?? '');
      setNote(e.note ?? '');
    }
  }, [state]);

  if (!state) return null;
  const isMove = state.mode === 'move';
  const existingRecipeId = state.mode !== 'create' ? state.entry.recipeId : null;
  const recipeId = kind === 'recipe' ? (recipe?.id ?? existingRecipeId) : null;
  const valid =
    Boolean(date) &&
    (kind === 'recipe' ? Boolean(recipeId) : title.trim().length > 0) &&
    (slot !== 'CUSTOM' || customSlotName.trim());

  const submit = () => {
    if (!valid) return;
    onSubmit({
      date,
      slot,
      customSlotName: slot === 'CUSTOM' ? customSlotName.trim() : null,
      recipeId,
      title: kind === 'text' ? title.trim() : null,
      servings: servings.trim() || null,
      note: note.trim() || null,
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')} fullscreenOnMobile>
        <DialogHeader>
          <DialogTitle>{t(`mealPlan.entry.${state.mode}`)}</DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {!isMove && (
            <>
              <Tabs value={kind} onValueChange={(v) => setKind(v as 'recipe' | 'text')}>
                <TabsList aria-label={t('mealPlan.entry.kind')}>
                  <TabsTrigger value="recipe">{t('mealPlan.entry.recipe')}</TabsTrigger>
                  <TabsTrigger value="text">{t('mealPlan.entry.freeText')}</TabsTrigger>
                </TabsList>
              </Tabs>
              {kind === 'recipe' && (
                <div>
                  {(recipe || recipeTitle) && (
                    <p className="mb-2 text-sm">
                      {t('mealPlan.entry.selected')}:{' '}
                      <strong>{recipe?.title ?? recipeTitle}</strong>
                    </p>
                  )}
                  <RecipeSearchList
                    multiple={false}
                    selected={recipe ? [recipe.id] : existingRecipeId ? [existingRecipeId] : []}
                    onToggle={setRecipe}
                  />
                </div>
              )}
              {kind === 'text' && (
                <FormField label={t('mealPlan.entry.title')} required>
                  <Input
                    value={title}
                    maxLength={200}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="meal-entry-title"
                  />
                </FormField>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('mealPlan.entry.date')} required>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </FormField>
            <div>
              <Label htmlFor="entry-slot">{t('mealPlan.entry.slot')}</Label>
              <Select value={slot} onValueChange={(v) => setSlot(v as MealSlot)}>
                <SelectTrigger id="entry-slot" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...STANDARD_SLOTS, 'CUSTOM' as MealSlot].map((s) => (
                    <SelectItem key={s} value={s}>
                      {mealSlotLabel(t, s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {slot === 'CUSTOM' && (
            <FormField label={t('mealPlan.entry.customSlotName')} required>
              <Input
                value={customSlotName}
                maxLength={40}
                onChange={(e) => setCustomSlotName(e.target.value)}
              />
            </FormField>
          )}
          {!isMove && (
            <>
              <FormField label={t('common.servings')} optionalText={t('common.optional')}>
                <Input
                  inputMode="decimal"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                />
              </FormField>
              <FormField label={t('mealPlan.entry.note')} optionalText={t('common.optional')}>
                <Textarea
                  rows={2}
                  maxLength={1000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </FormField>
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!valid} data-testid="meal-entry-save">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
